import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, Optional, QueryList, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, catchError, combineLatest, filter, map, of, take, takeUntil, tap } from 'rxjs';
import { ErrorHandlerService } from 'src/app/api/error-handler.service';
import { GeneralService } from 'src/app/api/routes/general.service';
import { ProductService } from 'src/app/api/routes/product.service';
import { BaseComponent } from 'src/app/components/base.component';
import { CloseAction, OpenAction, SpinnerState } from 'src/app/store/spinner';
import { ProductModels } from 'src/app/core/models/index';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { PRODUCT_ENUM, AUTO_DRAW_ENUM, EnumState } from 'src/app/core/enum/index';
import { AlertModalState } from 'src/app/store/alert-modal';
import { AlertService } from 'src/app/shared/alert.service';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss']
})
export class ProductListComponent extends BaseComponent implements OnInit {

	// 環境
	public environment = environment;
	public currentUrl = '';

	// 產品卡片
	@ViewChildren('productCard') productCards!: QueryList<ElementRef>;
	public customCardHeight: number = 0;

	// 篩選開關狀態
	public filterIsClose = true;

	// 產品類型
	public productType = 1;

	// 當篩選沒用厚度時，硬度預設值
	public DEFAULT_THICKNESS = 2;

	public CONACT_PERSON = PRODUCT_ENUM.CONACT_PERSON;

	public TIM_TYPE = AUTO_DRAW_ENUM.TIM_TYPE;
	public THERMAL_COEFFICIENT = AUTO_DRAW_ENUM.THERMAL_COEFFICIENT;

	public filter: {
		name: string,	// 篩選項目名稱
		memo: string,	// 備註
		productType: number[],	// 會有該篩選項目的產品類別
		options: number[],	// 會有該篩選項目的產品類別
		min: number | null,	// 所選的最小值
		max: number | null,	// 所選的最大值
	}[] 
	= []

	// 產品list
	public productList: ProductModels.Product[] = [];
	private enums: any;

	private onPageShow = (event: PageTransitionEvent) => {
		if (event.persisted) {
		  this.initializeData();
		}
	  };
	  
	private isValidFilter(filter: any[]): boolean {
		return filter.every(item => 
			Array.isArray(item.productType) && 
			item.productType.every((type: number) => 
				Object.values(this.TIM_TYPE).includes(type)
			)
		);
	}
	

	constructor(
		private generalServ: GeneralService,
		private productServ: ProductService,
		private cd: ChangeDetectorRef,
		private spinnerState: Store<SpinnerState>,
		private errServ: ErrorHandlerService,
		private route: ActivatedRoute,
		private router: Router,
		private translate: TranslateService,
		private alertModalState: Store<AlertModalState>,
		private alertServ: AlertService,
		@Optional() @Inject('APP_INITIALIZED') private appInitialized$: BehaviorSubject<boolean>
	) { super(); }

	ngOnInit(): void {
		// console.log('product-list');
		this.currentUrl = this.router.url;
		
		// 監聽 bfcache 恢復
		window.addEventListener('pageshow', (event) => {
		  if (event.persisted) {
			this.initializeData();
		  }
		});
	  
		this.initializeData();
	}
	  
	override ngOnDestroy(): void {
		window.removeEventListener('pageshow', this.onPageShow);
		super.ngOnDestroy();
	}
	  
	private initializeData(): void {
		// 確保基本 enum 值
		this.TIM_TYPE = this.TIM_TYPE || AUTO_DRAW_ENUM.TIM_TYPE;
		this.THERMAL_COEFFICIENT = this.THERMAL_COEFFICIENT || AUTO_DRAW_ENUM.THERMAL_COEFFICIENT;
	  
		// 監聽 enum 更新
		combineLatest([
			AUTO_DRAW_ENUM.isLoaded$,
			AUTO_DRAW_ENUM.enumSubject
		]).pipe(
			filter(([isLoaded, enums]) => isLoaded && !!enums && Object.keys(enums).length > 0),
			takeUntil(this.unsubscribe$)
		).subscribe({
			next: ([_, data]) => {
				this.enums = data;
				this.TIM_TYPE = this.enums.TIM_TYPE || this.TIM_TYPE;
				this.THERMAL_COEFFICIENT = this.enums.THERMAL_COEFFICIENT || this.THERMAL_COEFFICIENT;
				this.initializeFilter();
				this.cd.detectChanges();
			}
		});
	}
	  
	private initializeFilter(): void {
		const filterStr = sessionStorage.getItem('filter');
		const filterTypeStr = sessionStorage.getItem('filterType');
		
		if (filterTypeStr && filterStr) {
			try {
				const savedFilter = JSON.parse(filterStr);
				if (this.isValidFilter(savedFilter)) {
				this.filter = savedFilter;
				this.productType = parseInt(filterTypeStr);
				this.changeWmkOptions();
				this.fetchProductList(true);
				return;
				}
			} catch (e) {
				console.error('Error parsing saved filter:', e);
			}
		}
	  
		// 設置預設值
		this.filter = [
			{
				name: "WMK",
				memo: "",
				productType: [this.TIM_TYPE['TIM'], this.TIM_TYPE['TMP']],
				options: [],
				min: null,
				max: null
			},
			{
				name: "THICKNESS",
				memo: "",
				productType: [this.TIM_TYPE['TIM']],
				options: [0.3, 0.5, 1.0, 2.0, 3.0, 4.0, 5.0, 6.0],
				min: null,
				max: null
			},
			{
				name: "HARDNESS",
				memo: "HARDNESS",
				productType: [this.TIM_TYPE['TIM']],
				options: [20, 25, 30, 35, 40, 45, 60],
				min: null,
				max: null
			}
		];
		
		this.changeWmkOptions();
		this.fetchProductList(true);
	  }


	/**
	 * 產品查詢API
	 */
	public fetchProductList(isInit: boolean = false){
		// console.log('fetchproduct')

		const type = this.productType;

		// 當沒設置厚度卻用硬度篩選時，厚度預設為2
		let thickness_min = this.filter[1].min;
		let thickness_max = this.filter[1].max;
		const hardness_min = this.filter[2].min;
		const hardness_max = this.filter[2].max;
		const hasThick = (thickness_min || thickness_max)
		const hasHardness = (hardness_min || hardness_max)
		if (hasHardness && !hasThick){
			thickness_min = this.DEFAULT_THICKNESS
			thickness_max = this.DEFAULT_THICKNESS
		}
		let param = {
			type: type,
			wmk_min: this.filter[0].min,  // 導熱係數最小
			wmk_max: this.filter[0].max,  // 導熱係數最大
			thickness_min: thickness_min,  // 厚度最小（導熱硅膠才有）
			thickness_max: thickness_max,  // 厚度最大（導熱硅膠才有）
			hardness_min: hardness_min, // 硬度最小（導熱硅膠才有）
			hardness_max: hardness_max,  // 硬度最大（導熱硅膠才有）
			language_type: 1,  // 語言類型
			is_return_feature: 1  // 是否回傳特型(0 否 1 是)
		}
		this.spinnerState.dispatch(new OpenAction('Loading...'));
		this.productServ.fetchList(JSON.stringify(param)).pipe(
			takeUntil(this.unsubscribe$),
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((data: ProductModels.Product[]) => {
				this.productList = data
				// 暫存篩選
				sessionStorage.setItem('filter', JSON.stringify(this.filter));
				sessionStorage.setItem('filterType', this.productType.toString());
				this.spinnerState.dispatch(new CloseAction());
				this.cd.markForCheck();

				if(this.productList.length === 0){
					this.customCardHeight = 350;
					const msg = this.translate.instant("PRODUCT.FILTER.ALERT", {
						mailto: `<a href="mailto:${this.CONACT_PERSON}">${this.CONACT_PERSON}</a>`
					});
					this.alertServ.openAlert(msg);
				} else {
					// 設定客製化卡片高度
					setTimeout(() => {
						this.customCardHeight = this.productCards.last.nativeElement.offsetHeight;
					}, 0);
				}
			}),
		).subscribe()
	}

	/**
	 * 重置篩選
	 */
	public resetFilter(){
		this.changeWmkOptions();
		this.filter.forEach(item => {
			item.min = null
			item.max = null
		})
	}

	/**
	 * 改變導熱係數選單
	 */
	public changeWmkOptions() {
		const typeKey = Object.keys(this.TIM_TYPE).find(key => {
			return this.TIM_TYPE[key] === this.productType
		});
		if (typeKey) {
			this.filter[0].options = this.THERMAL_COEFFICIENT[typeKey];
		}
	}

	/**
	 * 將enum object value轉為list
	 * @param obj `Object`: enum object
	 * @returns `any[]`: enum object value list
	 */
	public sortEnumObj(obj: Object): any[]{
		const sortedList = Object.values(obj).sort((a, b) => a - b);
		return sortedList
	}

	/**
	 * 手機切換篩選開關狀態
	 */
	public toggleFilter(){
		this.filterIsClose = !this.filterIsClose
	}

	/**
	 * 導向自動繪圖參數頁面
	 */
	public nevigateToAudoDraw() {
		this.router.navigate(['/custom/product/autodraw'])
	}
}
